import * as t from 'io-ts'
import React, { FC, useContext, useEffect } from 'react'

import {
  DragNDropStateContext,
  DragEffectCallback,
} from './drag-n-drop-provider'

type DragEffectDragGroupIds = (string | string[])[]
type DragGroupEffectProps = {
  dragEffectGroupIds: DragEffectDragGroupIds
  dragEffectCallback?: DragEffectCallback
  forceEnableDragAndDrop?: boolean
}

/**
 * The **DragGroupEffect** component may be used as a descendant of **DragNDropProvider**
 *   to register side-effects when drag & drop related events fire for drag groups
 *
 * @param props.dragEffectGroupIds
 *   specifies an array of identifier for the drag groups for which to have this effect
 *   an identifier which is itself an array (of string identifiers) will only fire if
 *   all of the identifiers within that subarray fire
 * @param props.dragEffectCallback
 *   optionally specifies a callback to be registered for updates made based on the drag & drop event type
 *     and the relevant elements
 */
const DragGroupEffect: FC<DragGroupEffectProps> = ({
  dragEffectGroupIds,
  dragEffectCallback,
  children,
}) => {
  const dragNDropContext = useContext(DragNDropStateContext)
  if (dragNDropContext === undefined) {
    throw new Error(
      'The context-based React component, DragGroupEffect, must have DragNDropProvider as an ancestor!',
    )
  }
  const { registerCallback, deregisterCallback } = dragNDropContext
  useEffect(() => {
    if (!dragEffectCallback) {
      return
    }
    const registeredCallbacks: Record<string, DragEffectCallback> = {}
    dragEffectGroupIds.forEach((dragGroupId) => {
      registeredCallbacks[JSON.stringify(dragGroupId)] = registerCallback(
        dragGroupId,
        dragEffectCallback,
      )
    })
    return () => {
      Object.entries(registeredCallbacks).forEach(([dragGroupIdJson, cb]) => {
        const parsed = JSON.parse(dragGroupIdJson)
        if (t.union([t.array(t.string), t.string]).is(parsed)) {
          deregisterCallback(parsed, cb)
        }
      })
    }
  }, [
    registerCallback,
    deregisterCallback,
    dragEffectGroupIds,
    dragEffectCallback,
  ])
  return <>{children}</>
}

export default DragGroupEffect
